import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Sheet, Surface } from "@vd/ui";
import { Building } from "lucide-react";
import { useMemo, useState } from "react";
import { errorMessage } from "../../api/client";
import { companiesQuery, ops, providerAccessQuery, qk } from "../../api/ops";
import type { OpsCompany } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { toast } from "../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  HelpTip,
  Loading,
  PageHeader,
  Switch,
} from "../../components/ui";
import { fmtDate, fmtNumber, relTime } from "../../lib/format";

export const Route = createFileRoute("/_app/companies")({
  staticData: { crumb: { label: "Companies", parents: [] } },
  loader: ({ context }) => context.queryClient.prefetchQuery(companiesQuery),
  component: CompaniesPage,
});

/** The seven provider kinds in display order, with their names (ADR 0050). */
export const PROVIDER_KINDS: { kind: string; label: string }[] = [
  { kind: "telnyx", label: "Telnyx" },
  { kind: "xai", label: "xAI" },
  { kind: "xai_research", label: "xAI for lead generation" },
  { kind: "brave", label: "Brave Search" },
  { kind: "storage", label: "Storage" },
  { kind: "email", label: "Microsoft sender" },
  { kind: "anthropic", label: "Anthropic" },
];

const LABEL: Record<string, string> = Object.fromEntries(PROVIDER_KINDS.map((p) => [p.kind, p.label]));

/** "None", "Storage", "Telnyx, xAI" or "Telnyx, xAI +5". */
export function ownAccountsLine(kinds: readonly string[]): string {
  const names = PROVIDER_KINDS.filter((p) => kinds.includes(p.kind)).map((p) => p.label);
  if (names.length === 0) return "None";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

/** "Telnyx", "Telnyx and xAI", "Telnyx, xAI and Storage". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const ABOUT_OWN =
  "The company enters and pays for these accounts itself. Removing one hides it; stored keys keep working until removed.";

const col = createColumnHelper<OpsCompany>();
const num = (v: number) => <span className="tnum">{fmtNumber(v)}</span>;

function CompaniesPage() {
  const q = useQuery(companiesQuery);
  const [open, setOpen] = useState<OpsCompany | null>(null);
  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Company",
        cell: (c) => <span className="font-medium">{c.getValue()}</span>,
      }),
      col.accessor("status", {
        header: "Status",
        cell: (c) =>
          c.getValue() === "active" ? (
            <Badge tone="ok">Active</Badge>
          ) : (
            <Badge tone="neutral">Disabled</Badge>
          ),
      }),
      col.accessor((c) => ownAccountsLine(c.own_providers ?? []), {
        id: "own_providers",
        header: "Own accounts",
        cell: (c) => {
          const v = c.getValue();
          return <span className={v === "None" ? "text-ink-3" : "text-ink-2"}>{v}</span>;
        },
      }),
      col.accessor("members", {
        header: "Members",
        meta: { align: "right" },
        cell: (c) => num(c.getValue()),
      }),
      col.accessor("invited", {
        header: "Invited",
        meta: { align: "right" },
        cell: (c) => num(c.getValue()),
      }),
      col.accessor("admins", { header: "Admins", meta: { align: "right" }, cell: (c) => num(c.getValue()) }),
      col.accessor("calls_30d", {
        header: "Calls, 30 days",
        meta: { align: "right" },
        cell: (c) => num(c.getValue()),
      }),
      col.accessor((c) => c.last_seen_at ?? "", {
        id: "last_seen_at",
        header: "Last seen",
        cell: (c) => (
          <span className="whitespace-nowrap text-ink-2">{relTime(c.row.original.last_seen_at)}</span>
        ),
      }),
      col.accessor("created_at", {
        header: "Created",
        cell: (c) => <span className="whitespace-nowrap text-ink-2">{fmtDate(c.getValue())}</span>,
      }),
    ],
    [],
  );
  return (
    <>
      <PageHeader title="Companies" />
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <DataTable
            data={q.data.items}
            columns={columns}
            getRowId={(c) => c.id}
            initialSorting={[{ id: "name", desc: false }]}
            onRowClick={setOpen}
            rowLabel={(c) => c.name}
            empty={<EmptyState icon={<Building />} title="No companies yet" />}
          />
        )}
      </Card>
      {open ? <ProviderAccessSheet key={open.id} company={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** A company's provider access: seven switches, Save; a removal asks first. */
function ProviderAccessSheet({ company, onClose }: { company: OpsCompany; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery(providerAccessQuery(company.id));
  const [draft, setDraft] = useState<string[] | null>(null);
  const [confirm, setConfirm] = useState(false);
  const saved = q.data?.kinds ?? null;
  const current = draft ?? saved ?? [];
  const removed = saved ? saved.filter((k) => !current.includes(k)) : [];
  const dirty =
    !!draft && !!saved && (draft.length !== saved.length || draft.some((k) => !saved.includes(k)));

  const save = useMutation({
    mutationFn: (kinds: string[]) => ops.setProviderAccess(company.id, { kinds }),
    onSuccess: (next) => {
      qc.setQueryData(qk.providerAccess(company.id), next);
      qc.setQueryData<{ items: OpsCompany[] }>(qk.companies, (old) =>
        old
          ? { items: old.items.map((c) => (c.id === company.id ? { ...c, own_providers: next.kinds } : c)) }
          : old,
      );
      void qc.invalidateQueries({ queryKey: qk.companies });
      setConfirm(false);
      toast("Saved");
      onClose();
    },
    onError: (e) => {
      setConfirm(false);
      toast(errorMessage(e), "danger");
    },
  });

  const ordered = PROVIDER_KINDS.map((p) => p.kind).filter((k) => current.includes(k));
  const onSave = () => {
    if (removed.length) setConfirm(true);
    else save.mutate(ordered);
  };
  const toggle = (kind: string, on: boolean) =>
    setDraft(on ? [...current.filter((k) => k !== kind), kind] : current.filter((k) => k !== kind));

  return (
    <>
      <Sheet
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        title={company.name}
        className="max-w-[480px]"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!dirty} busy={save.isPending} onClick={onSave}>
              Save
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <h3 className="flex items-center gap-1.5 font-medium text-title">
            Own accounts
            <HelpTip label="About own accounts" text={ABOUT_OWN} />
          </h3>
          {q.isPending ? (
            <Loading />
          ) : q.isError ? (
            <ErrorState error={q.error} onRetry={() => q.refetch()} />
          ) : (
            <Surface radius="xl" className="flex flex-col divide-y divide-hairline">
              {PROVIDER_KINDS.map((p) => (
                <div key={p.kind} className="flex min-h-12 items-center gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1 text-ink text-ui">{p.label}</span>
                  <Switch
                    label={p.label}
                    checked={current.includes(p.kind)}
                    onChange={(v) => toggle(p.kind, v)}
                  />
                </div>
              ))}
            </Surface>
          )}
        </div>
      </Sheet>
      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        tone="danger"
        size="sm"
        title={`Hide ${joinNames(removed.map((k) => LABEL[k] ?? k))} from ${company.name}?`}
        description="Their admins stop seeing these settings, costs and carrier limits."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" busy={save.isPending} onClick={() => save.mutate(ordered)}>
              Hide
            </Button>
          </>
        }
      />
    </>
  );
}
